import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  TrendingUp,
  Wallet,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Calendar,
  Users,
  Loader2,
} from 'lucide-react';
import { getFinancialDashboard } from '../../services/MasterAdmin/accountantService';

type PeriodType = 'daily' | 'weekly' | 'monthly' | 'custom';

const AccountantDashboardPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('daily');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const params: any = { period: selectedPeriod };
        if (selectedPeriod === 'custom' && customStartDate && customEndDate) {
          params.startDate = customStartDate;
          params.endDate = customEndDate;
        }
        const response = await getFinancialDashboard(params);
        setDashboardData(response.data);
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError(err.response?.data?.message || 'Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedPeriod, customStartDate, customEndDate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600 dark:text-primary-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="space-y-6">
        <p className="text-gray-500 dark:text-gray-400">No dashboard data available.</p>
      </div>
    );
  }

  const getRevenueTitle = () => {
    switch (selectedPeriod) {
      case 'daily':
        return 'Today\'s Revenue';
      case 'weekly':
        return 'Week Revenue';
      case 'monthly':
        return 'Month Revenue';
      case 'custom':
        return 'Custom Period Revenue';
      default:
        return 'Revenue';
    }
  };

  const getRevenueValue = () => {
    switch (selectedPeriod) {
      case 'daily':
        return dashboardData.totalRevenue.today;
      case 'weekly':
        return dashboardData.totalRevenue.week;
      case 'monthly':
        return dashboardData.totalRevenue.month;
      case 'custom':
        return dashboardData.totalRevenue.custom || 0;
      default:
        return dashboardData.totalRevenue.today;
    }
  };

  const stats = [
    {
      title: getRevenueTitle(),
      value: formatCurrency(getRevenueValue()),
      icon: DollarSign,
      color: 'bg-blue-500',
    },
    {
      title: 'Total Commission',
      value: formatCurrency(dashboardData.totalCommission),
      subtitle:
        (dashboardData.totalBaseCommission ?? 0) > dashboardData.totalCommission + 0.009
          ? `Gross commission before offer: ${formatCurrency(dashboardData.totalBaseCommission)}`
          : undefined,
      icon: Wallet,
      color: 'bg-orange-500',
    },
    ...(dashboardData.totalOfferDiscount > 0
      ? [
          {
            title: 'Platform Offers',
            value: `−${formatCurrency(dashboardData.totalOfferDiscount)}`,
            subtitle: dashboardData.totalOfferAbsorbed
              ? `Absorbed ${formatCurrency(dashboardData.totalOfferAbsorbed)}`
              : undefined,
            icon: TrendingUp,
            color: 'bg-emerald-500',
          },
        ]
      : []),
    {
      title: 'Platform Fees',
      value: formatCurrency(dashboardData.platformFeesCollected),
      icon: TrendingUp,
      color: 'bg-indigo-500',
    },
    {
      title: 'GST Collected',
      value: formatCurrency(dashboardData.gstCollected),
      icon: TrendingUp,
      color: 'bg-purple-500',
    },
    {
      title: 'Total Recovered Charges',
      value: formatCurrency(dashboardData.totalRecoveredCharges),
      icon: Wallet,
      color: 'bg-red-500',
    },
  ];

  const settlementStats = [
    {
      title: 'Completed',
      value: dashboardData.completedSettlements,
      icon: CheckCircle2,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
    },
    {
      title: 'Pending',
      value: dashboardData.pendingSettlements,
      icon: AlertCircle,
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    },
    {
      title: 'Failed',
      value: dashboardData.failedSettlements,
      icon: XCircle,
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header with Period Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setSelectedPeriod('daily')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPeriod === 'daily'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Daily
          </button>
          <button
            onClick={() => setSelectedPeriod('weekly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPeriod === 'weekly'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Weekly
          </button>
          <button
            onClick={() => setSelectedPeriod('monthly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPeriod === 'monthly'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setSelectedPeriod('custom')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPeriod === 'custom'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Custom
          </button>
          {selectedPeriod === 'custom' && (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white sm:w-auto"
              />
              <span className="hidden text-gray-500 dark:text-gray-400 sm:inline">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white sm:w-auto"
              />
            </div>
          )}
        </div>
        <div className="flex w-full sm:w-auto">
          <button className="w-full rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700 sm:w-auto">
            Export Report
          </button>
        </div>
      </div>

      {/* Revenue Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {stat.value}
                  </p>
                  {'subtitle' in stat && stat.subtitle && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.subtitle}</p>
                  )}
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Settlement Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {settlementStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className={`${stat.bgColor} rounded-lg p-6 border border-gray-200 dark:border-gray-700`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.title} Settlements
                  </p>
                  <p className={`text-3xl font-bold ${stat.color} mt-2`}>
                    {stat.value}
                  </p>
                </div>
                <Icon className={`w-8 h-8 ${stat.color}`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Upcoming Settlement Cycle */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white sm:text-xl">
            Upcoming Settlement Cycle
          </h3>
          <button className="text-left text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 sm:text-right">
            View Details →
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Settlement Date</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {new Date(dashboardData.upcomingSettlementCycle.date).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Estimated Amount</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatCurrency(dashboardData.upcomingSettlementCycle.estimatedAmount)}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Vendors Count</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {dashboardData.upcomingSettlementCycle.vendorCount} Vendors
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left">
            <Wallet className="w-5 h-5 text-primary-600 dark:text-primary-400 mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">Initiate Settlement</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Start new settlement cycle
            </p>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left">
            <TrendingUp className="w-5 h-5 text-primary-600 dark:text-primary-400 mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">Generate Report</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Export financial reports
            </p>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left">
            <AlertCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">Review Pending</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Check pending settlements
            </p>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left">
            <XCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">Failed Payouts</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Retry failed transactions
            </p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountantDashboardPage;
